import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
interface AddATCModalProps {
  onClose: () => void;
  onSave: (atcData: any) => void;
}
const AddATCModal: React.FC<AddATCModalProps> = ({
  onClose,
  onSave
}) => {
  const [atcData, setAtcData] = useState({
    atcCode: '',
    description: '',
    amount: ''
  });
  const [errors, setErrors] = useState({
    atcCode: '',
    amount: ''
  });
  // Common ATC codes for quick selection
  const commonATCCodes = [{
    code: 'YQ',
    description: 'Fuel Surcharge'
  }, {
    code: 'YR',
    description: 'Insurance Surcharge'
  }, {
    code: 'SG',
    description: 'Singapore Departure Tax'
  }, {
    code: 'HK',
    description: 'Hong Kong Airport Fee'
  }, {
    code: 'G4',
    description: 'Service Fee'
  }];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setAtcData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // If selecting an ATC code, auto-fill the description
    if (name === 'atcCode') {
      const selectedCode = commonATCCodes.find(item => item.code === value);
      if (selectedCode) {
        setAtcData(prev => ({
          ...prev,
          description: selectedCode.description
        }));
      }
    }
  };
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    if (!atcData.atcCode) {
      newErrors.atcCode = 'ATC code is required';
      isValid = false;
    }
    if (!atcData.amount) {
      newErrors.amount = 'Amount is required';
      isValid = false;
    } else if (isNaN(parseFloat(atcData.amount))) {
      newErrors.amount = 'Amount must be a number';
      isValid = false;
    }
    setErrors(prev => ({
      ...prev,
      ...newErrors
    }));
    return isValid;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Format data for submission
      const formattedData = {
        ...atcData,
        amount: parseFloat(atcData.amount),
        id: Date.now() // Generate a temporary ID
      };
      onSave(formattedData);
      onClose();
    }
  };
  return <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center" data-id="element-103">
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4" data-id="element-104">
        <div className="flex items-start justify-between p-4 border-b border-gray-200" data-id="element-105">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-106">
            Add ATC (Additional Ticket Charges)
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" aria-label="Close" data-id="element-107">
            <XIcon className="h-6 w-6" data-id="element-108" />
          </button>
        </div>
        <form onSubmit={handleSubmit} data-id="element-109">
          <div className="p-6 space-y-4" data-id="element-110">
            <div data-id="element-111">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-112">
                ATC Code <span className="text-red-500" data-id="element-113">*</span>
              </label>
              <select name="atcCode" value={atcData.atcCode} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.atcCode ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} data-id="element-114">
                <option value="" data-id="element-115">Select ATC Code</option>
                {commonATCCodes.map(code => <option key={code.code} value={code.code} data-id="element-116">
                    {code.code} - {code.description}
                  </option>)}
                <option value="other" data-id="element-117">Other (Custom)</option>
              </select>
              {errors.atcCode && <p className="mt-1 text-sm text-red-600" data-id="element-118">{errors.atcCode}</p>}
            </div>
            {atcData.atcCode === 'other' && <div data-id="element-119">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-120">
                  Custom ATC Code <span className="text-red-500" data-id="element-121">*</span>
                </label>
                <input type="text" name="atcCode" value={atcData.atcCode === 'other' ? '' : atcData.atcCode} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g. F1" maxLength={2} data-id="element-122" />
                <p className="mt-1 text-xs text-gray-500" data-id="element-123">
                  Enter a 1-2 character code
                </p>
              </div>}
            <div data-id="element-124">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-125">
                Description
              </label>
              <input type="text" name="description" value={atcData.description} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g. Fuel Surcharge" data-id="element-126" />
            </div>
            <div data-id="element-127">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-128">
                Amount <span className="text-red-500" data-id="element-129">*</span>
              </label>
              <div className="relative" data-id="element-130">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-131">
                  <span className="text-gray-500 sm:text-sm" data-id="element-132">$</span>
                </div>
                <input type="text" name="amount" value={atcData.amount} onChange={handleChange} className={`mt-1 block w-full pl-7 pr-3 py-2 border ${errors.amount ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="0.00" data-id="element-133" />
              </div>
              {errors.amount && <p className="mt-1 text-sm text-red-600" data-id="element-134">{errors.amount}</p>}
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200" data-id="element-135">
            <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-136">
              Add ATC
            </button>
            <button type="button" onClick={onClose} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-137">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>;
};
export default AddATCModal;